import { EventTypes } from '@candulabs/craft';
import React, { ReactElement, useEffect, useState } from 'react';
import styled from 'styled-components';

import { toCssSelector } from '@candu-ai/stylesheet/utils';
import Node from 'craft/components/Node';
import Placeholder from 'craft/components/Placeholder';
import {
  useEditor,
  useEditorNode,
  useEditorSelector,
  useNodeContext,
  useNodeEvents,
  useSelection,
} from 'craft/hooks';
import { AccordionArrow } from 'craft/icons';
import { CraftNode, Resolver, ToolboxComponentSections } from 'craft/types';

import { CheckBox } from './CheckBox';
import { EditableTitle } from './EditableTitle';
import { CheckListItemSettings } from './toolbox';

// NOTE: Container styles are preserved in document
const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
`;

const Content = styled.div<{ hovered: boolean }>`
  border-top-left-radius: ${(props: any) => (props.hovered ? '0px' : '4px')};
  border-bottom-left-radius: ${(props: any) => (props.hovered ? '0px' : '4px')};
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const Header = styled.div<{ expanded: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  border-bottom: ${(props: any) => (props.expanded ? 'var(--checklist-item-border-width)' : '0px')}
    solid ${(props: any) => (props.expanded ? 'var(--checklist-item-border-color)' : '#ffffff00')};
  transition: all 0.25s ease-out 0s;
`;

const Children = styled.div<{ expanded: boolean }>`
  margin-left: 8px;
  display: flex;
  flex-direction: column;
  padding: ${(props: any) => (props.expanded ? '16px' : '0px 16px')};
  max-height: ${(props: any) => (props.expanded ? 'none' : '0px')};
  overflow: hidden;
  transition: all 0.35s ease-out 0s;
`;

const Title = styled(EditableTitle)`
  margin-right: 16px;
`;

const Expander = styled.div`
  cursor: pointer;
  border-left: var(--checklist-item-border-width) solid var(--checklist-item-border-color);
  height: 100%;
  width: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface Props {
  title: string;
  children?: ReactElement | ReactElement[];
}

export const CheckListItem: Resolver<Props> = ({ title, children }: Props) => {
  const { id } = useNodeContext();
  const { setProps } = useEditorNode(id);
  const { query, actions } = useEditor();
  const { selection } = useSelection();
  // Automatically expand this item when another node is dragged over
  const nodeEvents = useNodeEvents(id);
  const [expanded, setExpanded] = useState(false);

  const { isDraggedOver, canAcceptNode } = useEditorSelector(
    ({ indicator, events: { dragged } }) => {
      const currentPlacement = indicator?.placement;
      const isNewNode = !dragged.size;
      return {
        isDraggedOver:
          currentPlacement?.currentNode?.id === id || currentPlacement?.parent?.id === id,
        canAcceptNode: isNewNode || query.node(id).isDroppable(Array.from(dragged)),
      };
    },
  );
  useEffect(() => {
    if (isDraggedOver && canAcceptNode && !expanded) {
      setExpanded(true);
    }
  }, [isDraggedOver, canAcceptNode, expanded]);

  useEffect(() => {
    if (expanded) {
      return;
    }
    const descendants = query.node(id).descendants(true);
    const aSelectedNodeIsADescendant = selection.all.find(
      (selectedId: string) => descendants.indexOf(selectedId) > -1,
    );
    if (aSelectedNodeIsADescendant) {
      setExpanded(true);
    }
  }, [selection.all, expanded]);

  const onEdit = (value: string) => setProps({ title: value });

  const itemClassName = toCssSelector('checklist', {
    className: 'item',
    output: 'html',
  });

  const checkBoxClassName = toCssSelector('checklist', {
    className: 'checkbox',
    output: 'html',
  });

  const checkBoxCheckedClassName = toCssSelector('checklist', {
    className: 'checkboxChecked',
    output: 'html',
  });

  return (
    <Node is={Container} className={itemClassName}>
      <Content hovered={nodeEvents.selected || nodeEvents.dragged}>
        <Header expanded={expanded}>
          <CheckBox className={`${checkBoxClassName} ${checkBoxCheckedClassName}`} />
          <Title
            tagName="span"
            key={id}
            html={title}
            onEdit={onEdit}
            onClick={() => actions.setEventNodes(EventTypes.selected, [id])}
            disabled={!nodeEvents.selected}
          />
          <Expander
            data-craft-ignore-events
            onClick={() => {
              if (!nodeEvents.selected) {
                actions.setEventNodes(EventTypes.selected, [id]);
              }
              setExpanded(!expanded);
            }}
          >
            <AccordionArrow
              color="var(--checklist-item-border-color)"
              direction={expanded ? 'up' : 'down'}
            />
          </Expander>
        </Header>
        <Children expanded={expanded}>{children || <Placeholder />}</Children>
      </Content>
    </Node>
  );
};

CheckListItem.craft = {
  displayName: 'Checklist Item',
  name: 'CheckListItem',
  isCanvas: true,
  rules: {
    canMoveIn: (incomingNodes: CraftNode[]) =>
      incomingNodes.every((node) => !node.data.name.startsWith('CheckList')),
    canDrop: (target: CraftNode) => target.data.name === 'CheckListGroup',
  },
};

CheckListItem.resize = {
  shouldDisableHandlers: true,
};

CheckListItem.toolbox = {
  component: [CheckListItemSettings],
  schema: {
    [ToolboxComponentSections.Border]: {
      isVisible: false,
    },
    [ToolboxComponentSections.Shadow]: {
      isVisible: false,
    },
  },
};
